/**
 * Listado de clappers del dashboard de Learning (doc 14/09 §2, "1.3 Listado").
 * Columnas por pestaña: team (Mi equipo), new (New Clappers), old (Old Clappers).
 * "Detalle" abre la ficha con la tarjetita Obligatorio/Opcional y la señal de
 * visualización real de los videos.
 */
import { useTranslation } from 'react-i18next';
import { Eye, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { LearningDashboardRow } from '@/../../shared/people-learning';
import { STATUS_BADGE, antiguedad, fmtDate, fmtMs, plazoLabel, rowPlazo, statusLabel } from './learning-dash-utils';
import { EngagementDot } from './EngagementDot';
import { usePagination } from '@/hooks/usePagination';

export type TableMode = 'team' | 'new' | 'old';

export function ClapperTable({ rows, mode, today, onDetail }: {
  rows: LearningDashboardRow[];
  mode: TableMode;
  today: string;
  onDetail: (email: string) => void;
}) {
  const { t } = useTranslation();
  const k = 'people_learning.dash.table';
  const showLeader = mode !== 'team';
  const cols = 8 + (showLeader ? 1 : 0);
  const { pageItems: pageRows, footer: paginationFooter } = usePagination(rows, 25);
  return (
    <>
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t(`${k}.clapper`, 'Clapper')}</TableHead>
            <TableHead>{t(`${k}.area`, 'Área')}</TableHead>
            <TableHead>{t(`${k}.country`, 'País')}</TableHead>
            {showLeader && <TableHead>{t(`${k}.leader`, 'Líder')}</TableHead>}
            <TableHead>{t(`${k}.start_date`, 'Fecha de ingreso')}</TableHead>
            <TableHead>{mode === 'old' ? t(`${k}.antiguedad`, 'Antigüedad') : t(`${k}.plazo`, 'Plazo')}</TableHead>
            <TableHead>{t(`${k}.status`, 'Estado')}</TableHead>
            <TableHead className="text-right">{t(`${k}.progress`, 'Avance')}</TableHead>
            <TableHead className="text-right">{t(`${k}.time`, 'Tiempo')}</TableHead>
            <TableHead className="text-right">{t(`${k}.detail`, 'Detalle')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={cols + 1} className="py-8 text-center text-muted-foreground">
                <Users className="mx-auto mb-2 h-5 w-5 opacity-50" />
                {t(`${k}.empty`, 'Sin personas para este filtro.')}
              </TableCell>
            </TableRow>
          )}
          {pageRows.map((r) => {
            const plazo = plazoLabel(t, rowPlazo(r, today));
            const mandatory = r.category === 'new_clapper' && r.status !== 'no_elegible';
            return (
              <TableRow key={r.email}>
                <TableCell>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.role || r.email}</div>
                </TableCell>
                <TableCell className="text-sm">{r.area || '—'}</TableCell>
                <TableCell className="text-sm">{r.country || '—'}</TableCell>
                {showLeader && <TableCell className="text-sm">{r.leaderName || '—'}</TableCell>}
                <TableCell className="text-sm tabular-nums">{fmtDate(r.startDate)}</TableCell>
                {mode === 'old' ? (
                  <TableCell className="text-sm">{antiguedad(t, r.startDate, today)}</TableCell>
                ) : (
                  <TableCell className={`text-sm ${plazo.className}`}>{plazo.text}</TableCell>
                )}
                <TableCell>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[r.status]}`}>
                    {statusLabel(t, r.status)}
                  </span>
                  {r.testMode && <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-300" title={t(`${k}.test_mode_hint`, 'Todos los niveles desbloqueados para pruebas: no cuenta en las métricas')}>{t(`${k}.test_mode`, 'prueba')}</span>}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">{Math.round(r.progressPct * 100)}%</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{fmtMs(r.elapsedMs)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <EngagementDot engagement={r.engagement} />
                    <Badge variant={mandatory ? 'default' : 'secondary'} className="hidden text-[10px] lg:inline-flex">
                      {mandatory ? t(`${k}.mandatory`, 'Obligatorio') : t(`${k}.optional`, 'Opcional')}
                    </Badge>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onDetail(r.email)}>
                      <Eye className="mr-1 h-3.5 w-3.5" /> {t(`${k}.see_detail`, 'Ver detalle')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
    {paginationFooter}
    </>
  );
}
